import * as React from 'react';
import * as PropTypes from 'prop-types';
import { Button, ButtonGroup, Toolbar, ToolbarSpacer } from '@progress/kendo-react-buttons';
import { TextBox, InputSuffix } from '@progress/kendo-react-inputs';
import { Pager } from '@progress/kendo-react-data-tools';
import { Upload } from '@progress/kendo-react-upload';
import { ComboBox } from '@progress/kendo-react-dropdowns';
import { Loader } from '@progress/kendo-react-indicators';
import { classNames, validatePackage, shouldShowValidationUI, WatermarkOverlay } from '@progress/kendo-react-common';
import { useLocalization } from '@progress/kendo-react-intl';
import { zoomOutIcon, zoomInIcon, pointerIcon, handIcon, searchIcon, folderOpenIcon, downloadIcon, printIcon, convertLowercaseIcon, arrowUpIcon, arrowDownIcon, xIcon } from '@progress/kendo-svg-icons';
import 'pdfjs-dist/build/pdf.worker.entry';
import { packageMetadata } from './package-metadata';
import { Scroller, SearchService, DEFAULT_ZOOM_LEVEL, loadPDF, print, download, goToNextSearchMatch, goToPreviousSearchMatch, reloadDocument, calculateZoomLevel, removeChildren, scrollToPage, currentPage } from '@progress/kendo-pdfviewer-common';
import { messages, close, download as downloadM, enablePanning, enableSelection, matchCase as matchCaseM, nextMatch, open, prevMatch, print as printM, search, zoomIn, zoomOut, popupBlocked, actualWidth, fitToWidth, fitToPage } from './messages';
const toolNames = [
    'pager', 'spacer', 'zoomInOut', 'zoom', 'selection', 'spacer', 'search', 'open', 'download', 'print'
];
// Correct declaration but typedoc does not recognize it.
// export type PDFViewerTool = typeof toolNames[number];
const defaultProps = {
    minZoom: 0.5,
    maxZoom: 4,
    tools: [...toolNames],
    zoomRate: 0.25,
    zoomLevels: [
        { id: 1, priority: 1, value: 1, text: 'Actual width', type: 'ActualWidth', locationString: actualWidth },
        { id: 2, priority: 2, value: 1, text: 'Fit to width', type: 'FitToWidth', locationString: fitToWidth },
        { id: 3, priority: 3, value: 1, text: 'Fit to page', type: 'FitToPage', locationString: fitToPage },
        { id: 4, priority: 4, value: 0.5, text: '50%', type: '' },
        { id: 5, priority: 5, value: 0.75, text: '75%', type: '' },
        { id: 6, priority: 100, value: 1, text: '100%', type: '' },
        { id: 7, priority: 7, value: 1.25, text: '125%', type: '' },
        { id: 8, priority: 8, value: 1.5, text: '150%', type: '' },
        { id: 9, priority: 9, value: 2, text: '200%', type: '' },
        { id: 10, priority: 10, value: 3, text: '300%', type: '' },
        { id: 11, priority: 11, value: 4, text: '400%', type: '' }
    ],
    defaultZoom: DEFAULT_ZOOM_LEVEL
};
const navigation = [
    '.k-toolbar > button',
    '.k-toolbar .k-combobox > input',
    '.k-toolbar .k-button-group > button',
    '.k-toolbar .k-pager > a',
    '.k-toolbar .k-pager input'
];
const sortByPriority = (a, b) => {
    if (a.priority > b.priority) {
        return -1;
    }
    else if (a.priority < b.priority) {
        return 1;
    }
    return 0;
};
/**
 * Represents the [KendoReact PDFViewer component]({% slug api_pdf-viewer_pdfviewerprops %}).
 */
export const PDFViewer = React.forwardRef((props, ref) => {
    validatePackage(packageMetadata);
    const showLicenseWatermark = shouldShowValidationUI(packageMetadata);
    const { zoom, zoomLevels = defaultProps.zoomLevels, defaultZoom = defaultProps.defaultZoom, minZoom = defaultProps.minZoom, maxZoom = defaultProps.maxZoom, zoomRate = defaultProps.zoomRate } = props;
    const loc = useLocalization();
    const pagesRef = React.useRef(null);
    const [stateZoom, setStateZoom] = React.useState(defaultZoom);
    const currentZoom = zoom !== undefined ? zoom : stateZoom;
    const currentZoomLevel = zoomLevels.slice().sort(sortByPriority).find(z => z.value === currentZoom) ||
        { text: (currentZoom * 100) + '%', value: currentZoom, id: currentZoom, locationString: '' };
    if (currentZoomLevel.locationString) {
        currentZoomLevel.text = loc.toLanguageString(currentZoomLevel.locationString, messages[currentZoomLevel.locationString]);
    }
    const [hasDocument, setHasDocument] = React.useState(false);
    const [loading, setLoading] = React.useState(true);
    const [skip, setSkip] = React.useState(0);
    const [enabledSelection, setEnabledSelection] = React.useState(false);
    const [showSearch, setShowSearch] = React.useState(false);
    const [matches, setMatches] = React.useState(0);
    const [currentMatch, setCurrentMatch] = React.useState(0);
    const [matchCase, setMatchCase] = React.useState(false);
    const [searchText, setSearchText] = React.useState('');
    const refObj = React.useMemo(() => ({}), []);
    refObj.currentZoom = currentZoom;
    refObj.props = props;
    const setScaleFactor = React.useCallback((scaleFactor) => {
        if (pagesRef.current) {
            pagesRef.current.style.setProperty('--scale-factor', String(scaleFactor));
        }
    }, []);
    const target = React.useRef(null);
    const pdfViewerRef = React.useRef(null);
    React.useImperativeHandle(target, () => ({
        element: pdfViewerRef.current,
        props,
        pages: refObj.pages,
        document: refObj.document
    }), []);
    React.useImperativeHandle(ref, () => target.current);
    const triggerOnLoad = React.useCallback(() => {
        if (refObj.props.onLoad) {
            const loadEvent = { target: target.current };
            refObj.props.onLoad.call(undefined, loadEvent);
        }
    }, []);
    const triggerOnDownload = React.useCallback((blob, fileName, saveOptions) => {
        if (props.onDownload) {
            const downloadEvent = {
                target: target.current,
                blob,
                fileName,
                saveOptions
            };
            const result = props.onDownload.call(undefined, downloadEvent);
            return result === false ? true : false;
        }
        return false;
    }, [props.onDownload]);
    const initScroller = React.useCallback(() => {
        var _a;
        if (refObj.scroller) {
            refObj.scroller.destroy();
        }
        refObj.scroller = new Scroller((_a = pagesRef.current) === null || _a === void 0 ? void 0 : _a.parentNode, {
            filter: '.k-page',
            events: {}
        });
        refObj.scroller.enablePanEventsTracking();
    }, []);
    const initSearch = React.useCallback((wrapper) => {
        const pagesTextLayers = Array.from(wrapper.querySelectorAll('.k-text-layer'));
        refObj.search = new SearchService({
            textContainers: pagesTextLayers || [],
            highlightClass: 'k-search-highlight',
            highlightMarkClass: 'k-search-highlight-mark',
            charClass: 'k-text-char'
        });
    }, []);
    refObj.done = React.useCallback(({ pdfPages, pdfDoc, zoom: documentZoom }) => {
        refObj.document = pdfDoc;
        refObj.pages = pdfPages;
        refObj.zoom = documentZoom;
        initScroller();
        setLoading(false);
        setHasDocument(true);
        triggerOnLoad();
        if (pagesRef.current) {
            scrollToPage(pagesRef.current, 0);
        }
    }, []);
    refObj.error = React.useCallback((reason) => {
        refObj.document = null;
        refObj.pages = [];
        setLoading(false);
        setHasDocument(false);
        if (props.onError) {
            const errorEvent = {
                error: typeof reason === 'string' ? { message: reason } : reason,
                target: target.current
            };
            props.onError.call(undefined, errorEvent);
        }
    }, [props.onError]);
    React.useEffect(() => {
        if (pagesRef.current) {
            if (props.url || props.data || props.arrayBuffer) {
                setLoading(true);
                removeChildren(pagesRef.current);
                loadPDF({
                    url: props.url,
                    data: props.data,
                    arrayBuffer: props.arrayBuffer,
                    dom: pagesRef.current,
                    zoom: refObj.currentZoom,
                    done: refObj.done,
                    error: refObj.error
                });
                setScaleFactor(refObj.currentZoom);
            }
            else {
                refObj.document = null;
                refObj.pages = [];
                setHasDocument(false);
                setLoading(false);
                removeChildren(pagesRef.current);
            }
        }
    }, [props.url, props.data, props.arrayBuffer]);
    const reload = React.useCallback((pdfDoc, zoomLev) => {
        if (pagesRef.current) {
            setLoading(true);
            removeChildren(pagesRef.current);
            reloadDocument({
                pdfDoc,
                zoom: zoomLev,
                dom: pagesRef.current,
                done: (pdfPages) => {
                    refObj.pages = pdfPages;
                    refObj.zoom = zoomLev;
                    setLoading(false);
                },
                error: refObj.error
            });
        }
    }, []);
    React.useEffect(() => {
        setScaleFactor(currentZoom);
        if (pagesRef.current && refObj.document && currentZoom !== refObj.zoom) {
            reload(refObj.document, currentZoom);
        }
    }, [currentZoom, reload]);
    React.useEffect(() => {
        return () => {
            if (refObj.scroller) {
                refObj.scroller.destroy();
            }
            if (refObj.search) {
                refObj.search.destroy();
            }
            refObj.document = null;
            refObj.pages = [];
        };
    }, []);
    const onSearch = React.useCallback(() => {
        setShowSearch(true);
        initSearch(pagesRef.current);
    }, []);
    const onSearchTextChange = React.useCallback((e) => {
        const text = e.value;
        const currentMatches = refObj.search.search({ text, matchCase });
        setCurrentMatch(currentMatches.length ? 1 : 0);
        setMatches(currentMatches.length);
        setSearchText(text);
    }, [matchCase]);
    const onMatchCaseClick = React.useCallback(() => {
        const currentMatches = refObj.search.search({ text: searchText, matchCase: !matchCase });
        setCurrentMatch(currentMatches.length ? 1 : 0);
        setMatches(currentMatches.length);
        setMatchCase(!matchCase);
    }, [matchCase, searchText]);
    const onNextMatch = React.useCallback(() => {
        goToNextSearchMatch(refObj);
        setCurrentMatch((currentMatch + 1) > matches ? 1 : (currentMatch + 1));
    }, [currentMatch, matches]);
    const onPrevMatch = React.useCallback(() => {
        goToPreviousSearchMatch(refObj);
        setCurrentMatch((currentMatch - 1) < 1 ? matches : (currentMatch - 1));
    }, [currentMatch, matches]);
    const onClose = React.useCallback(() => {
        refObj.search.destroy();
        setCurrentMatch(0);
        setMatches(0);
        setMatchCase(false);
        setSearchText('');
        setShowSearch(false);
    }, []);
    const onTextBoxKeyDown = React.useCallback((e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            goToNextSearchMatch(refObj);
            setCurrentMatch((currentMatch + 1) > matches ? 1 : (currentMatch + 1));
        }
        else if (e.key === 'Escape') {
            onClose();
        }
    }, [currentMatch, matches]);
    const onPageChange = React.useCallback((e) => {
        if (pagesRef.current) {
            const nextPage = e.skip;
            scrollToPage(pagesRef.current, nextPage);
            const pageEvent = {
                page: nextPage + 1,
                target: target.current,
                syntheticEvent: e.syntheticEvent
            };
            if (props.onPageChange) {
                props.onPageChange.call(undefined, pageEvent);
            }
        }
        setSkip(e.skip);
    }, [skip, props.onPageChange]);
    const onScroll = React.useCallback((e) => {
        if (pdfViewerRef.current) {
            const nextPage = currentPage(pdfViewerRef.current);
            if (nextPage !== skip) {
                setSkip(nextPage);
                const pageEvent = {
                    page: nextPage + 1,
                    target: target.current,
                    syntheticEvent: e
                };
                if (props.onPageChange) {
                    props.onPageChange.call(undefined, pageEvent);
                }
            }
        }
    }, [skip, props.onPageChange]);
    const onZoomIn = React.useCallback((e) => {
        const newZoom = Math.min(refObj.currentZoom + zoomRate, maxZoom);
        if (newZoom !== refObj.currentZoom && refObj.document) {
            setStateZoom(newZoom);
            if (props.onZoom) {
                const zoomEvent = {
                    zoom: newZoom,
                    target: target.current,
                    syntheticEvent: e
                };
                props.onZoom.call(undefined, zoomEvent);
            }
        }
    }, [zoomRate, maxZoom, props.onZoom]);
    const onZoomOut = React.useCallback((e) => {
        const newZoom = Math.max(refObj.currentZoom - zoomRate, minZoom);
        if (newZoom !== refObj.currentZoom && refObj.document) {
            setStateZoom(newZoom);
            if (props.onZoom) {
                const zoomEvent = {
                    zoom: newZoom,
                    target: target.current,
                    syntheticEvent: e
                };
                props.onZoom.call(undefined, zoomEvent);
            }
        }
    }, [zoomRate, minZoom, props.onZoom]);
    const onZoomLevelChange = React.useCallback((e) => {
        const item = e.value === null ? { text: '100%', value: 1, id: 100 } : Object.assign({}, e.value);
        if (item.value === undefined) {
            const parsedText = parseFloat(item.text);
            if (typeof parsedText === 'number' && !Number.isNaN(parsedText)) {
                item.value = parsedText / 100;
            }
            else {
                item.value = 1;
            }
        }
        let newZoom = item ? calculateZoomLevel(item.value, item.type, refObj.currentZoom, pagesRef.current) : 1;
        newZoom = Math.round(newZoom * 100) / 100;
        if (refObj.currentZoom !== newZoom && refObj.document) {
            setStateZoom(newZoom);
            if (props.onZoom) {
                const zoomEvent = {
                    zoom: newZoom,
                    target: target.current,
                    syntheticEvent: e.syntheticEvent
                };
                props.onZoom.call(undefined, zoomEvent);
            }
        }
    }, [props.onZoom]);
    const onTextSelection = React.useCallback(() => {
        refObj.scroller.disablePanEventsTracking();
        setEnabledSelection(true);
    }, []);
    const onPinning = React.useCallback(() => {
        refObj.scroller.enablePanEventsTracking();
        setEnabledSelection(false);
    }, []);
    const onDownload = React.useCallback(() => {
        download({
            pdf: refObj.document,
            error: refObj.error
        }, props.saveFileName, props.saveOptions, triggerOnDownload);
    }, [props.url, props.data, props.arrayBuffer, props.saveFileName, props.saveOptions, triggerOnDownload]);
    const onPrint = React.useCallback(() => {
        setLoading(true);
        const onError = (e) => {
            refObj.error(typeof (e ? e.message || e : null) === 'string' ? e.message || e : loc.toLanguageString(popupBlocked, messages[popupBlocked]));
        };
        const onDone = () => {
            setLoading(false);
        };
        print(refObj.pages, onDone, onError);
    }, []);
    const onAdd = React.useCallback((e) => {
        const st = e.newState;
        if (st[0] && st[0].getRawFile) {
            const file = st[0].getRawFile();
            file.arrayBuffer().then((arrayBuffer) => {
                if (pagesRef.current) {
                    setLoading(true);
                    removeChildren(pagesRef.current);
                    const currentZoomValue = refObj.props.zoom === undefined ? defaultZoom : refObj.props.zoom;
                    loadPDF({
                        arrayBuffer,
                        dom: pagesRef.current,
                        zoom: currentZoomValue,
                        done: refObj.done,
                        error: refObj.error
                    });
                    setStateZoom(currentZoomValue);
                }
            });
        }
    }, [defaultZoom]);
    const onFileOpen = React.useCallback((e) => {
        const targetEl = e.target;
        if (targetEl instanceof Element && targetEl.parentNode) {
            const toolbarEl = targetEl.closest('.k-toolbar');
            const input = toolbarEl && toolbarEl.querySelector('.k-upload input');
            if (input) {
                input.click();
            }
        }
    }, []);
    const loader = (loading && React.createElement("div", { className: "k-loader-container k-loader-container-md k-loader-top" },
        React.createElement("div", { className: "k-loader-container-overlay k-overlay-light" }),
        React.createElement("div", { className: "k-loader-container-inner " },
            React.createElement(Loader, { size: 'large' }))));
    const zoomInOut = (React.createElement(ButtonGroup, null,
        React.createElement(Button, { className: 'k-toolbar-button', title: loc.toLanguageString(zoomOut, messages[zoomOut]), disabled: currentZoom <= minZoom || !hasDocument, onClick: onZoomOut, icon: 'zoom-out', svgIcon: zoomOutIcon }),
        React.createElement(Button, { className: 'k-toolbar-button', title: loc.toLanguageString(zoomIn, messages[zoomIn]), disabled: currentZoom >= maxZoom || !hasDocument, onClick: onZoomIn, icon: 'zoom-in', svgIcon: zoomInIcon })));
    const zoomComboBoxTool = (React.createElement(ComboBox, { disabled: !hasDocument, data: (zoomLevels).map(level => (Object.assign(Object.assign({}, level), { text: level.locationString
                ? loc.toLanguageString(level.locationString, messages[level.locationString])
                : level.text }))), dataItemKey: 'id', textField: 'text', value: hasDocument ? currentZoomLevel : null, allowCustom: true, onChange: onZoomLevelChange }));
    const pagerTool = (React.createElement(Pager, { previousNext: true, type: 'input', skip: skip, take: 1, total: refObj.pages ? refObj.pages.length : 0, info: false, onPageChange: onPageChange }));
    const spacer = React.createElement(ToolbarSpacer, null);
    const selectionTool = (React.createElement(ButtonGroup, null,
        React.createElement(Button, { className: 'k-toolbar-button', title: loc.toLanguageString(enableSelection, messages[enableSelection]), icon: 'pointer', svgIcon: pointerIcon, disabled: !hasDocument, togglable: true, selected: enabledSelection && hasDocument, onClick: onTextSelection }),
        React.createElement(Button, { className: 'k-toolbar-button', title: loc.toLanguageString(enablePanning, messages[enablePanning]), icon: 'hand', svgIcon: handIcon, disabled: !hasDocument, togglable: true, selected: !enabledSelection && hasDocument, onClick: onPinning })));
    const searchTool = (React.createElement(Button, { className: 'k-toolbar-button', title: loc.toLanguageString(search, messages[search]), icon: 'search', svgIcon: searchIcon, disabled: !hasDocument, onClick: onSearch }));
    const openTool = (React.createElement(React.Fragment, null,
        React.createElement(Button, { className: 'k-toolbar-button', title: loc.toLanguageString(open, messages[open]), icon: 'folder-open', svgIcon: folderOpenIcon, onClick: onFileOpen }),
        React.createElement("div", { style: { display: 'none' } },
            React.createElement(Upload, { restrictions: { allowedExtensions: ['.pdf'] }, onAdd: onAdd, autoUpload: false, defaultFiles: [], multiple: false, accept: ".pdf,.PDF", withCredentials: false }))));
    const downloadTool = (React.createElement(Button, { className: 'k-toolbar-button', title: loc.toLanguageString(downloadM, messages[downloadM]), icon: 'download', svgIcon: downloadIcon, disabled: !hasDocument, onClick: onDownload }));
    const printTool = (React.createElement(Button, { className: 'k-toolbar-button', title: loc.toLanguageString(printM, messages[printM]), icon: 'print', svgIcon: printIcon, disabled: !hasDocument, onClick: onPrint }));
    const tools = {
        pager: pagerTool,
        spacer,
        zoomInOut,
        zoom: zoomComboBoxTool,
        selection: selectionTool,
        search: searchTool,
        open: openTool,
        download: downloadTool,
        print: printTool
    };
    const buttons = (props.tools || defaultProps.tools).map((tool) => tools[tool]);
    const toolbar = (React.createElement(Toolbar, { buttons: navigation }, ...buttons));
    const content = (React.createElement("div", { className: classNames('k-canvas k-pdf-viewer-canvas k-pos-relative k-overflow-auto', {
            'k-enable-text-select': enabledSelection,
            'k-enable-panning': !enabledSelection
        }), onScroll: onScroll },
        showSearch && (React.createElement("div", { className: 'k-search-panel k-pos-sticky k-top-center' },
            React.createElement(TextBox, { value: searchText, onChange: onSearchTextChange, placeholder: loc.toLanguageString(search, messages[search]), autoFocus: true, onKeyDown: onTextBoxKeyDown, suffix: () => (React.createElement(InputSuffix, null,
                    React.createElement(Button, { icon: "convert-lowercase", svgIcon: convertLowercaseIcon, title: loc.toLanguageString(matchCaseM, messages[matchCaseM]), fillMode: "flat", togglable: true, selected: matchCase, onClick: onMatchCaseClick }))) }),
            React.createElement("span", { className: "k-search-matches" },
                React.createElement("span", null, currentMatch),
                " of ",
                React.createElement("span", null, matches)),
            React.createElement(Button, { title: loc.toLanguageString(prevMatch, messages[prevMatch]), fillMode: 'flat', icon: 'arrow-up', svgIcon: arrowUpIcon, disabled: matches === 0, onClick: onPrevMatch }),
            React.createElement(Button, { title: loc.toLanguageString(nextMatch, messages[nextMatch]), fillMode: 'flat', icon: 'arrow-down', svgIcon: arrowDownIcon, disabled: matches === 0, onClick: onNextMatch }),
            React.createElement(Button, { title: loc.toLanguageString(close, messages[close]), fillMode: 'flat', icon: 'x', svgIcon: xIcon, onClick: onClose }))),
        React.createElement("div", { ref: pagesRef, className: 'k-pdf-viewer-pages' })));
    return (React.createElement("div", { className: 'k-pdf-viewer', style: props.style, ref: pdfViewerRef },
        props.onRenderLoader ? props.onRenderLoader.call(undefined, loader || null) : loader,
        props.onRenderToolbar ? props.onRenderToolbar.call(undefined, toolbar) : toolbar,
        props.onRenderContent ? props.onRenderContent.call(undefined, content) : content,
        showLicenseWatermark && React.createElement(WatermarkOverlay, null)));
});
PDFViewer.displayName = 'KendoReactPDFViewer';
PDFViewer.propTypes = {
    url: PropTypes.string,
    data: PropTypes.string,
    arrayBuffer: PropTypes.any,
    typedArray: PropTypes.any,
    style: PropTypes.object,
    saveFileName: PropTypes.string,
    saveOptions: PropTypes.object,
    tools: PropTypes.arrayOf(PropTypes.oneOf(toolNames).isRequired),
    zoomLevels: PropTypes.arrayOf(PropTypes.any),
    zoom: PropTypes.number,
    defaultZoom: PropTypes.number,
    minZoom: PropTypes.number,
    maxZoom: PropTypes.number,
    zoomRate: PropTypes.number,
    onError: PropTypes.func,
    onLoad: PropTypes.func,
    onDownload: PropTypes.func,
    onRenderToolbar: PropTypes.func,
    onRenderContent: PropTypes.func,
    onRenderLoader: PropTypes.func,
    onZoom: PropTypes.func
};
PDFViewer.defaultProps = defaultProps;
